import { startOfWeek, endOfWeek, eachDayOfInterval, format, isToday, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarEvent } from '@/services/calendarService';
import EventCard from './EventCard';
import DeadlineBadge from './DeadlineBadge';
import { useRef, useEffect } from 'react';

interface CalendarWeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  selectedDate: Date | null;
  onDateClick: (date: Date) => void;
}

export default function CalendarWeekView({
  currentDate,
  events,
  selectedDate,
  onDateClick,
}: CalendarWeekViewProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Scroll all'ora corrente al mount o cambio data
  useEffect(() => {
    if (scrollContainerRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      const hourElement = scrollContainerRef.current.querySelector(`[data-hour="${currentHour}"]`);
      if (hourElement) {
        hourElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentDate]);

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      const eventDate = format(new Date(event.start), 'yyyy-MM-dd');
      return eventDate === dateStr;
    });
  };

  const getHourEvents = (date: Date, hour: number) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      if (event.allDay) return false;
      const eventDate = new Date(event.start);
      const eventHour = eventDate.getHours();
      const eventDateStr = format(eventDate, 'yyyy-MM-dd');
      return eventDateStr === dateStr && eventHour === hour;
    });
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="space-y-4">
      {/* Intestazione giorni */}
      <div className="grid grid-cols-8 gap-2 border-b pb-2">
        <div className="text-sm font-semibold text-gray-600">Ora</div>
        {weekDays.map((day) => {
          const isDayToday = isToday(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const dayEvents = getEventsForDate(day);
          
          return (
            <div
              key={day.toISOString()}
              className={`text-center cursor-pointer rounded-lg p-2 transition-colors ${
                isDayToday ? 'bg-blue-100 border-2 border-blue-500' :
                isSelected ? 'bg-blue-50 border border-blue-300' :
                'hover:bg-gray-50'
              }`}
              onClick={() => onDateClick(day)}
            >
              <div className="text-xs text-gray-500 font-medium">
                {format(day, 'EEE', { locale: it })}
              </div>
              <div className={`text-lg font-semibold ${isDayToday ? 'text-blue-600' : 'text-gray-900'}`}>
                {format(day, 'd')}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {dayEvents.length} {dayEvents.length === 1 ? 'evento' : 'eventi'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Griglia oraria con scroll solo ore */}
      <div 
        ref={scrollContainerRef}
        className="border rounded-lg overflow-y-auto"
        style={{ maxHeight: '600px' }}
      >
        <div className="space-y-1">
          {hours.map((hour) => (
            <div key={hour} data-hour={hour} className="grid grid-cols-8 gap-2 items-start border-b last:border-b-0 py-1">
              <div className="text-xs text-gray-500 font-medium py-2 text-right pr-2 shrink-0">
                {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
              </div>
              {weekDays.map((day) => {
                const hourEvents = getHourEvents(day, hour);
                const isDayToday = isToday(day);
                
                return (
                  <div
                    key={`${day.toISOString()}-${hour}`}
                    className={`min-h-[60px] border rounded p-1 ${
                      isDayToday && new Date().getHours() === hour
                        ? 'bg-blue-50 border-blue-200' 
                        : isDayToday 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    {hourEvents.map((event) => (
                      <div
                        key={event.id}
                        className={`text-xs p-1 rounded mb-1 truncate ${
                          event.type === 'appointment' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                        title={event.title}
                      >
                        {format(new Date(event.start), 'HH:mm')} {event.title}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* All-day events */}
      {weekDays.some(day => getEventsForDate(day).filter(e => e.allDay).length > 0) && (
        <div className="mt-4 border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Eventi tutto il giorno</div>
          <div className="space-y-2">
            {weekDays.map((day) => {
              const dayEvents = getEventsForDate(day).filter(e => e.allDay);
              if (dayEvents.length === 0) return null;
              
              return (
                <div key={day.toISOString()} className="space-y-2">
                  <div className="text-xs font-medium text-gray-600">
                    {format(day, 'EEEE d MMMM', { locale: it })}
                  </div>
                  {dayEvents.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

